import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./WorkWithUs.css"; // or Projects.css

import useScrollRestoration from "../hooks/useScrollRestoration";


async function loadMasterData(filename, fallback = []) {

  const remoteUrl = `https://indodeftech.com/data/getData.php?file=${filename}`;
  try {
    const response = await fetch(remoteUrl, { cache: "no-store" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    return Array.isArray(data) ? data : fallback;
  } catch (err) {
    console.warn(`⚠️ Using fallback for ${filename}:`, err.message);
    return fallback;
  }
}

function Projects() {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [pageInfo, setPageInfo] = useState({ title: "", description: "", footnote: "" });

  const [industries, setIndustries] = useState([]);
  const [technologies, setTechnologies] = useState([]);
  const [stages, setStages] = useState([]);
  const [fundings, setFundings] = useState([]);

  const [filters, setFilters] = useState({
    industry: "",
    technology: "",
    stage: "",
    funding: "",
  });

  const [sortOption, setSortOption] = useState("newest");
  const [isSticky, setIsSticky] = useState(false);
  const navigate = useNavigate();

  // ✅ Load data
  useEffect(() => {
    async function fetchData() {
      const [ind, tech, stage, fund] = await Promise.all([
        loadMasterData("industryStages.json"),
        loadMasterData("technologies.json"),
        loadMasterData("projectStages.json"),
        loadMasterData("fundingStages.json"),
      ]);

      setIndustries(ind);
      setTechnologies(tech);
      setStages(stage);
      setFundings(fund);
    }

    fetch("https://indodeftech.com/api/projects")
      .then((r) => r.json())
      .then((data) => {
        const projects = data.lists || [];
        setProjects(projects);
        setFilteredProjects(projects);
        setPageInfo(data.page || {});
        document.title = data.page?.title || "Projects - IndoDef Tech";
      })
      .catch((err) => console.error("Failed to load projects:", err));

    fetchData();
  }, []);

  // ✅ Filtering & Sorting
  useEffect(() => {
    let filtered = projects.filter((p) => {
      return (
        (!filters.industry || p.industry_stage === filters.industry) &&
        (!filters.technology || p.technology === filters.technology) &&
        (!filters.stage || p.project_stage === filters.stage) &&
        (!filters.funding || p.funding_stage === filters.funding)
      );
    });

    // ✅ Sorting logic
    switch (sortOption) {
      case "oldest":
        filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;
      case "alphabetical":
        filtered.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case "popular":
        filtered.sort((a, b) => (b.hits || 0) - (a.hits || 0));
        break;
      default: // newest
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }

    setFilteredProjects(filtered);
  }, [filters, sortOption, projects]);

  // ✅ Sticky filter scroll handler
  useEffect(() => {
    const handleScroll = () => setIsSticky(window.scrollY > 200);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const getProjectImage = (proj) => {
    if (proj.images && typeof proj.images === "object") {
      if (proj.primary_image && proj.images[proj.primary_image]) {
        return proj.images[proj.primary_image];
      }
      const firstImage = Object.values(proj.images).find((url) => !!url);
      if (firstImage) return firstImage;
    }
    return proj.image || null;
  };

  const truncateText = (t, limit = 200) =>
    t && t.length > limit ? t.substring(0, limit) + "..." : t;

  const getCount = (key, value) =>
    projects.filter((p) => (p[key] || "").trim() === value.trim()).length;

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilter = (key) => {
    setFilters((prev) => ({ ...prev, [key]: "" }));
  };


  useScrollRestoration("projects_scroll");


  return (
    <div className="work-container w3-animate-opacity">
      {/* Header */}
      <header className="work-header w3-center">
        <h2>{pageInfo.title || "Our Projects"}</h2>
        <p>{pageInfo.description || "Explore IndoDef Tech’s innovations and solutions."}</p>
      </header>

      {/* Filter Bar */}
      <div
        className={`filter-bar w3-row-padding w3-margin-bottom w3-center ${
          isSticky ? "sticky-filter w3-hide-small" : "w3-hide-small"
        }`}
      >
        {/* Industry */}
        <div className="w3-col l2 m6 s12 w3-margin-bottom">
          <select
            className="w3-select w3-border"
            value={filters.industry}
            onChange={(e) => handleFilterChange("industry", e.target.value)}
          >
            <option value="">All Industries</option>
            {industries.map((item) => (
              <option key={item} value={item}>
                {item} ({getCount("industry_stage", item) || 0})
              </option>
            ))}
          </select>
          {filters.industry && (
            <button className="clear-btn" onClick={() => clearFilter("industry")}>✖</button>
          )}
        </div>

        {/* Technology */}
        <div className="w3-col l2 m6 s12 w3-margin-bottom">
          <select
            className="w3-select w3-border"
            value={filters.technology}
            onChange={(e) => handleFilterChange("technology", e.target.value)}
          >
            <option value="">All Technologies</option>
            {technologies.map((item) => (
              <option key={item} value={item}>
                {item} ({getCount("technology", item) || 0})
              </option>
            ))}
          </select>
          {filters.technology && (
            <button className="clear-btn" onClick={() => clearFilter("technology")}>✖</button>
          )}
        </div>

        {/* Stage */}
        <div className="w3-col l2 m6 s12 w3-margin-bottom">
          <select
            className="w3-select w3-border"
            value={filters.stage}
            onChange={(e) => handleFilterChange("stage", e.target.value)}
          >
            <option value="">All Stages</option>
            {stages.map((item) => (
              <option key={item} value={item}>
                {item} ({getCount("project_stage", item) || 0})
              </option>
            ))}
          </select>
          {filters.stage && (
            <button className="clear-btn" onClick={() => clearFilter("stage")}>✖</button>
          )}
        </div>

        {/* Funding */}
        <div className="w3-col l2 m6 s12 w3-margin-bottom">
          <select
            className="w3-select w3-border"
            value={filters.funding}
            onChange={(e) => handleFilterChange("funding", e.target.value)}
          >
            <option value="">All Funding</option>
            {fundings.map((item) => (
              <option key={item} value={item}>
                {item} ({getCount("funding_stage", item) || 0})
              </option>
            ))}
          </select>
          {filters.funding && (
            <button className="clear-btn" onClick={() => clearFilter("funding")}>✖</button>
          )}
        </div>

        {/* ✅ Sort Dropdown */}
        <div className="w3-col l4 m12 s12 w3-margin-bottom">
          <select
            className="w3-select w3-border"
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
          >
            <option value="newest">Sort by: Newest</option>
            <option value="oldest">Sort by: Oldest</option>
            <option value="alphabetical">Sort by: Alphabetical (A–Z)</option>
            <option value="popular">Sort by: Popular (Most Hits)</option>
          </select>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="w3-row-padding work-grid">
        {filteredProjects.length > 0 ? (
          filteredProjects.map((proj) => {
            const img = getProjectImage(proj);
            return (
              <div key={proj.id} className="w3-col l4 m6 s12 w3-margin-bottom">
                <div  onClick={() => navigate(`/projects/${proj.id}`)}
                  className="w3-hover w3-pointer w3-cursor w3-card w3-padding w3-hover-shadow job-card"
                  style={{ height: "550px" }}
                >
                  {img && (
                    <img
                      src={img}
                      alt={proj.title}
                      className="w3-image w3-round-large"
                      style={{
                        width: "100%",
                        height: "220px",
                        objectFit: "cover",
                        marginBottom: "10px",
                      }}
                    />
                  )}
                  <h5 className="w3-text-blue w3-bold">{proj.title}</h5>

                  <div className="w3-center w3-row w3-small">
                    <div className="w3-col s4 m4 l4 w3-border">
                      {proj.industry_stage || "-"} <br />
                      <strong>Industry</strong>
                    </div>
                    <div className="w3-col s4 m4 l4 w3-border">
                      {proj.project_stage || "-"} <br />
                      <strong>Stage</strong>
                    </div>
                    <div className="w3-col s4 m4 l4 w3-border">
                      {proj.funding_stage || "Undisclosed"} <br />
                      <strong>Funding</strong>
                    </div>
                  </div>

                  <div className="w3-small" style={{ minHeight: "80px" }}>
                    {truncateText(proj.short_description || proj.description, 200)}
                  </div>

                  <div className="w3-col l12 ">
                    <button
                       className=" w3-col l12 w3-button w3-blue w3-border w3-round w3-hover-blue"
                      onClick={() => navigate(`/projects/${proj.id}`)}
                    >
                      Read More
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="w3-center w3-padding">
            <p>No projects found for selected filters.</p>
          </div>
        )}
      </div>

      <div className="w3-center w3-margin-top">
        <p>
          {pageInfo.footnote ||
            "Discover how IndoDef Tech delivers advanced defense and technology solutions."}
        </p>
      </div>
    </div>
  );
}

export default Projects;
